import { useEffect, useRef } from 'react'
import dayjs from 'dayjs'

import usePromiseDialog from '@shared/hooks/src/usePromiseDialog'

import ConfirmationV2 from '@components/Dialog/ConfirmationV2'

export const RECENT_ORDER_DAYS = 7

/** Whether a date falls within the recent order window */
export const isRecentOrder = (date) => {
  return Boolean(date) && dayjs().diff(dayjs(date), 'day') < RECENT_ORDER_DAYS
}

/**
 * Warns the provider, once per mount, that the patient already has an order created within the
 * last 7 days. The warning is informational: confirming calls `onConfirm` so the caller can point
 * the provider at the history it asked them to review, rejecting calls `onCancel` to exit.
 *
 * @param {Object} options
 * @param {boolean} options.isPending - True while the patient's most recent order is being fetched
 * @param {string} options.createdAt - Creation date of the patient's most recent order
 * @param {string} options.title - Title of the warning dialog
 * @param {string} options.description - Message of the warning dialog
 * @param {Function} [options.onConfirm] - Called when the provider acknowledges the warning
 * @param {Function} options.onCancel - Called when the provider backs out of the workflow
 */
export default function useRecentOrderWarning({ isPending, createdAt, title, description, onConfirm, onCancel }) {
  const shown = useRef(false)

  const showWarning = usePromiseDialog({
    component: ConfirmationV2,
    getProps: () => ({
      variant: 'warning',
      title,
      description,
      rejectLabel: 'Cancel',
      confirmLabel: 'Continue',
    }),
  })

  useEffect(() => {
    // `showWarning` is recreated on every render, so the ref is what keeps this to one dialog per mount
    if (isPending || shown.current || !isRecentOrder(createdAt)) return

    shown.current = true
    showWarning().then((confirmed) => {
      if (confirmed) onConfirm?.()
      else onCancel()
    })
  }, [isPending, createdAt, showWarning, onConfirm, onCancel])
}
